import { ReactElement, useRef, useState } from 'react'
import { WritableAtom, useAtom, useAtomValue } from 'jotai'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import { ModalState } from 'store/atoms/typings'
import { jotaiStore } from 'store'

import Activity from 'account-activity-mfe'
import { DisputeFeature, TransactionProps } from 'digital-disputes'
import { defaultCardArt } from 'const/assets'
import { activityModalContainerId } from 'const/modalContainerId'
import { useAccountSummary, useCreditAccounts } from 'const/syfApiQueries'
import findAccount from 'helpers/findAccount'
import getMFEConfig from 'helpers/getMFEConfig'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import useHandleNavigate from 'hooks/useHandleNavigate'
import i18n from 'strings/i18n'
import DrawerHeader from 'syf-component-library/ui/atoms/DrawerHeader'
import ModalHeader from 'syf-component-library/ui/patterns/SlideInModal/Header'
import { SlideInModalPortal } from 'syf-component-library/ui/patterns/SlideInModalPortal'
import { Inline, Inset, Stack } from 'syf-component-library/ui/spacing'

import { triggerAnalyticsEvent } from '@syf-utils/analytics'
import { triggerOnCloseAnalytics } from 'helpers/triggerOnCloseAnalytics'
import isAccountTypeSetpayOrPaylater from 'helpers/isAccountTypeSetpayOrPaylater'
import isAccountTypeSecuredInstallment from 'helpers/isAccountTypeSecuredInstallment'
import DisputesEligibilityPortal from 'App/DisputesEligibilityPortal'
import {
  DrawerHeaderWrapper,
  HorizontalBorder,
  ModalFooter,
  TabsContainer,
  TabsWrapper
} from './subcomponents'

const ActivityModal = (): ReactElement => {
  const [{ modalId, modalTriggerId }, updateModalStatus] =
    useAtom(modalStatusAtom)
  const selectedAccount = useAtomValue(selectedAccountAtom)

  const { data: { accounts = [] } = {} } = useCreditAccounts()
  const { data: accountSummary } = useAccountSummary(selectedAccount)
  const { handleNavigate } = useHandleNavigate()
  const headerRef = useRef(null)

  const account = findAccount(accounts, selectedAccount)
  const mmaConfig = getMFEConfig('mfe')
  const brandRefreshPhase = getBrandRefreshPhase()

  const accountLast4 = account?.info.accountLast4
  const isSetpayAccountOrPayLater = isAccountTypeSetpayOrPaylater(
    account?.product?.type
  )
  const isSecuredInstallmentAccount = isAccountTypeSecuredInstallment(
    account?.product?.type
  )

  const shouldHideViewAllActivity =
    isSetpayAccountOrPayLater || isSecuredInstallmentAccount

  const drawerProps = {
    title: accountSummary?.product?.displayName || 'Your Synchrony Account',
    subtitle: `${i18n({
      labels: isSetpayAccountOrPayLater ? 'loanEndingIn' : 'accountEndingIn'
    })} ${accountLast4}`,
    img: accountSummary?.card?.cardArtURL || defaultCardArt,
    fallbackImage: defaultCardArt,
    alt: ''
  }

  const [eligibleTransaction, setEligibleTransaction] =
    useState<TransactionProps | null>(null)

  const handleEligibilityChange = (transaction: TransactionProps | null) => {
    setEligibleTransaction(transaction)
  }

  const handleCloseModal = () => {
    updateModalStatus('', '')
    triggerOnCloseAnalytics()
  }

  const handleEscapeModal = () => {
    const questionnaireModal = document.getElementById('disputes-questionnaire')
    if (questionnaireModal) {
      // Prevent closing Activity Modal when Questionnaire is open
      return
    }

    updateModalStatus('', '')
    triggerOnCloseAnalytics()
  }

  const handleCloseAndNavigate = (feature: string) => {
    // Close Activity Modal and navigate to Dispute Center
    handleCloseModal()
    handleNavigate(feature)
  }

  return (
    <SlideInModalPortal
      id="activity-modal"
      escapeModalHandler={handleEscapeModal}
      containerId={activityModalContainerId(selectedAccount)}
      isOpen={modalId === 'Activity'}
      modalTriggerId={modalTriggerId}
      header={
        <ModalHeader
          closeModal={handleCloseModal}
          content={i18n({ labels: 'recentActivity' }) as string}
          data-test="modal-header"
          dataReason="x"
          dataType="activity"
          headerRef={headerRef}
        />
      }
      footer={
        <ModalFooter
          closeModal={handleCloseModal}
          hideViewAllActivity={shouldHideViewAllActivity}
        />
      }
    >
      <DrawerHeaderWrapper
        left="base"
        top="base"
        bottom="medium"
        data-test="modal-drawer"
      >
        <DrawerHeader {...drawerProps} />
      </DrawerHeaderWrapper>
      <Stack size="large" hideOnBreakpointDown="small" />
      <Inset vertical="base" hideOnBreakpointUp="small">
        <HorizontalBorder />
      </Inset>
      <TabsWrapper data-test="modal-tabs">
        <Inline size="xsmall" />
        <TabsContainer>
          <Activity
            config={mmaConfig}
            brandRefreshPhase={brandRefreshPhase}
            display="condensed"
            guidAccountId={selectedAccount}
            navigationHandler={handleCloseAndNavigate}
            triggerAnalyticsEvent={triggerAnalyticsEvent}
            totalTransactions="7"
            atoms={{
              modalStatusAtom: modalStatusAtom as unknown as WritableAtom<
                ModalState,
                [modalId: string, modalTriggerId?: string],
                void
              >
            }}
            store={jotaiStore}
            onEligibilityChange={handleEligibilityChange}
          />
          <DisputesEligibilityPortal
            transaction={eligibleTransaction}
            navigationHandler={handleCloseAndNavigate}
            returnToParent={(flow, transaction) => {
              handleEligibilityChange(null)
              if (flow === DisputeFeature.Questionnaire) {
                updateModalStatus('DisputeATransaction', '', { transaction })
              } else {
                updateModalStatus('')
              }
            }}
          />
        </TabsContainer>
        <Inline size="xsmall" />
      </TabsWrapper>
    </SlideInModalPortal>
  )
}

export default ActivityModal
